/*
 * roundSchedule — the single source of truth for how a round's
 * scheduled date + optional Morning/Afternoon session are encoded in
 * `scheduledStartAt` (TIMESTAMPTZ, no separate session column).
 *
 * Encoding, all in LOCAL time so the calendar day survives every
 * timezone round-trip:
 *   Morning   → 07:30
 *   Afternoon → 13:00
 *   Unset     → 12:00  (sentinel noon — never shown as a time)
 *
 * Legacy sentinels that also decode as "unset": 09:00 (the old
 * CreateRound default before this module existed).
 *
 * The session is OPTIONAL by design (Travis's spec): unset rounds show
 * just the date; morning/afternoon rounds get a label. Times are an
 * implementation detail and must never be rendered as a clock time.
 */

export type RoundSession = 'default' | 'morning' | 'afternoon';

/** date ("YYYY-MM-DD") + session → ISO string for the API. */
export function encodeRoundSchedule(dateYMD: string, session: RoundSession): string {
  const hhmm = session === 'morning' ? '07:30'
            : session === 'afternoon' ? '13:00'
            : '12:00';
  return new Date(`${dateYMD}T${hhmm}:00`).toISOString();
}

/** Stored ISO → which session chip was picked (for edit forms + labels). */
export function decodeRoundSession(iso: string | null | undefined): RoundSession {
  if (!iso) return 'default';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'default';
  const h = d.getHours();
  const m = d.getMinutes();
  // Sentinel "no session" times: new noon + legacy 9am default.
  if (m === 0 && (h === 12 || h === 9)) return 'default';
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 20) return 'afternoon';
  return 'default';
}

/** Stored ISO → "YYYY-MM-DD" in LOCAL time, for <input type="date">.
 *  Never slice the raw ISO string — that's the UTC date, which is a
 *  different calendar day for evening/early-morning local times. */
export function roundDateYMD(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Display label for a stored schedule, or null when no session set. */
export function sessionDisplayLabel(iso: string | null | undefined): 'Morning' | 'Afternoon' | null {
  const s = decodeRoundSession(iso);
  return s === 'morning' ? 'Morning' : s === 'afternoon' ? 'Afternoon' : null;
}
